<template>
	<div class="nav-menu-container" :class="{ 'mode-animating': isAnimating }">
		<sidebar-menu v-if="sidebarToggler" :menu="filteredRoutes" :width="'255px'" :showOneChild="true" :collapsed="collapsed" :hideToggle="true" :rtl="true" @update:collapsed="onToggleCollapse" @item-click="onItemClick">
			<template v-slot:header>
				<div class="navbar-header">
					<div class="brand-box">
						<div class="logo-container">
							<nk-image :title="`${$t('install.customer.f.customerCode')}: ${userAccounts.customerCode}`" :modelValue="logoWhite" :options="brandImageOptions" />
						</div>
						<span class="brand-title" v-if="!collapsed">{{ $t('nav-menu.brand-title') }}</span>
					</div>
					<div class="search-box" v-if="!collapsed">
						<icon icon="search" />
						<nk-input v-model="searchKeyword" class="nk-search" :options="inputOptions" ref="searchInput" @focus="() => isFocused = true" @blur="() => isFocused = false"></nk-input>
					</div>
					<div class="route-changer" v-if="!collapsed">
						<button type="button" class="route-changer-btn" :class="{ 'active': activeMode === 'all' }" :title="$t('nav-menu.all')" @click="setMode('all')">
							<i :class="activeMode === 'all' ? 'fas fa-layer-group' : 'fal fa-layer-group'"></i>
							<span class="btn-text">{{ $t('nav-menu.all') }}</span>
						</button>
						<button type="button" class="route-changer-btn" :class="{ 'active': activeMode === 'mostUsed', 'icon-only': activeMode !== 'mostUsed' }" :title="$t('nav-menu.most-used')" @click="setMode('mostUsed')">
							<i :class="activeMode === 'mostUsed' ? 'fas fa-thumbtack' : 'fal fa-thumbtack'"></i>
							<span class="btn-text" v-if="activeMode === 'mostUsed'">{{ $t('nav-menu.most-used') }}</span>
						</button>
						<button type="button" class="route-changer-btn" :class="{ 'active': activeMode === 'favorites', 'icon-only': activeMode !== 'favorites' }" :title="$t('nav-menu.favorites-title')" @click="setMode('favorites')">
							<i :class="activeMode === 'favorites' ? 'fas fa-star' : 'fal fa-star'"></i>
							<span class="btn-text" v-if="activeMode === 'favorites'">{{ $t('nav-menu.favorites') }}</span>
						</button>
						<button type="button" class="route-changer-btn" :class="{ 'active': activeMode === 'recent', 'icon-only': activeMode !== 'recent' }" :title="$t('nav-menu.recent')" @click="setMode('recent')">
							<i :class="activeMode === 'recent' ? 'fas fa-clock' : 'fal fa-clock'"></i>
							<span class="btn-text" v-if="activeMode === 'recent'">{{ $t('nav-menu.recent') }}</span>
						</button>
					</div>
					<div class="sidebar-empty-state" v-if="!collapsed && activeMode !== 'all' && filteredRoutes.length === 0">
						<div class="empty-content" v-if="activeMode === 'mostUsed'">
							<i class="fal fa-thumbtack empty-icon"></i>
							<span class="empty-text">{{ $t('nav-menu.no-most-used-yet') }}</span>
						</div>
						<div class="empty-content" v-else-if="activeMode === 'recent'">
							<i class="fal fa-clock empty-icon"></i>
							<span class="empty-text">{{ $t('nav-menu.no-recent-yet') }}</span>
						</div>
						<div class="empty-content" v-else-if="activeMode === 'favorites'">
							<i class="fal fa-star empty-icon"></i>
							<span class="empty-text">{{ $t('nav-menu.no-favorites-yet') }}</span>
							<button type="button" class="btn-open-settings" @click="openFavoriteSettings">
								<i class="fal fa-cog"></i>
								<span>{{ $t('nav-menu.set-favorites') }}</span>
							</button>
						</div>
					</div>
				</div>
			</template>
			<template v-slot:footer>
				<div class="navbar-footer" :class="{ 'collapsed': collapsed }">
					<div class="sidebar-version-wrapper" v-if="!collapsed" :title="`${$t('version-detail.version')}: ${displayVersion}`" @click="() => checkVersion(true)">
						<span class="version-text">{{ `${$t('version-detail.version')}: ${displayVersion}` }}</span>
					</div>
					<div class="sidebar-actions-wrapper">
						<!-- <button type="button" class="sidebar-action-btn announcements-btn" :title="$t('announcements.button-title')" @click.stop="openAnnouncementsModal">
							<icon icon="bullhorn" />
							<span v-if="hasUnseenAnnouncements" class="announcement-badge-dot"></span>
						</button> -->
						<button type="button" class="sidebar-action-btn clear-cache-btn" :title="$t('home.clear-app-cache')" @click.stop="appRefreshClicked">
							<icon icon="broom" />
						</button>
					</div>
				</div>
			</template>
		</sidebar-menu>
		<div class="sidebar-toggler-tab" :class="{ 'collapsed': collapsed }" @click="toggleCollapsed" :title="collapsed ? $t('nav-menu.open-menu') : $t('nav-menu.close-menu')">
			<i class="fal fa-chevron-double-right"></i>
		</div>
	</div>
</template>
<script>
	import _ from 'lodash'
	import $sc from '~/plugins/shortcut.collection.js'
	import $ from 'jquery'
	import { routes, findRoute } from '~/statics/routes'
	import { SidebarMenu } from 'vue-sidebar-menu'
	import { get, post } from '~/plugins/nikan-api'
	import Endpoints from '@nikan/endpoints'
	import 'vue-sidebar-menu/dist/vue-sidebar-menu.css'
	import dynamicReportInstance from '@/plugins/dynamic-report'
	import { markRaw } from 'vue'
	import { mapGetters } from 'vuex'
	import logoWhite from '@/assets/logo-white.png'
	import cacheCleaner from '~/components/cache/cache-cleaner.vue'
	import { hasUnseenAnnouncements } from '~/statics/announcements'
	import versionDetailsJson from '@/assets/resources/versionDetails.json'

	export default {
		props: {
			selectedItem: { type: Object },
			focusedItem: { type: Object },
			openedItem: { type: Object },
			hash: {},
			parentEl: {
				type: Function,
				default: () => { }
			},
			backVersion: { type: String, default: '' },
			frontVersion: { type: [String, Number], default: '' }
		},
		components: {
			SidebarMenu
		},
		emits: ['navigate', 'executeFunction', 'openedItemChanged', 'focusedItemChanged'],
		data() {
			return {
				logoWhite,
				sidebarToggler: false,
				routes,
				collapsed: false,
				activeMode: 'all', // 'all' | 'mostUsed' | 'favorites' | 'recent'
				isAnimating: false,
				searchKeyword: null,
				isFocused: false,
				isOpening: false,
				localBackVersion: '',
				localFrontVersion: '',
				brandImageOptions: {
					width: 26,
					height: 26
				}
			}
		},
		computed: {
			...mapGetters({
				'userAccounts': 'useraccount/currentUserDetails',
				'getRecentTabs': 'tabHistory/getRecentTabs',
				'getTabUsageCounts': 'tabHistory/getTabUsageCounts',
				'getActiveState': 'appStates/getActiveByKey'
			}),
			hasUnseenAnnouncements() {
				return hasUnseenAnnouncements(this.$store)
			},
			effectiveBackVersion() {
				return this.backVersion || this.localBackVersion || ''
			},
			effectiveFrontVersion() {
				return this.frontVersion || this.localFrontVersion || ''
			},
			displayVersion() {
				if (this.effectiveBackVersion && this.effectiveFrontVersion) {
					return `${this.effectiveBackVersion}.${this.effectiveFrontVersion}`
				}
				return this.effectiveBackVersion || this.effectiveFrontVersion || '...'
			},
			inputOptions() {
				return {
					mode: 'text',
					placeholder: this.$t('nav-menu.search')
				}
			},
			filteredRoutes() {
				const filterChild = (list) => {
					return list.reduce((a, c) => {
						if (c.items) a = [...a, ...filterChild(c.items)]
						else a.push(c)
						return a
					}, [])
				}

				let items = []
				if (this.activeMode === 'all') {
					const routes = _.cloneDeep(this.routes)
					items = this.searchKeyword ? filterChild(routes) : routes
				} else if (this.activeMode === 'mostUsed') {
					items = this.getMostUsedRoutes()
				} else if (this.activeMode === 'favorites') {
					items = this.getFavoriteRoutes()
				} else if (this.activeMode === 'recent') {
					items = this.getRecentRoutes()
				}

				items = _.uniqBy(items, 'id')

				// User Access
				this.routesAccessChecker(items)

				// Filter out items without commercial access or disabled if in flat mode
				if (this.activeMode !== 'all') {
					items = items.filter(x => x.hasCommercialAccess && !x.isDisabled)
				}

				// Search
				const isInChild = (list) => {
					if (!list || !list.length) {
						return false
					}

					for (let mi of list) {
						if (mi.menu && mi.menu.indexOf(this.searchKeyword) > -1) {
							return true
						} else if (isInChild(mi.items)) {
							return true
						}
					}

					return false
				}
				if (this.searchKeyword && this.searchKeyword !== '') {
					const keywords = this.searchKeyword.split(' ').filter(Boolean)

					const isKeywordInText = (route, keyword) => (route.menu && route.menu.indexOf(keyword) > -1) || isInChild(route.items) || (route.parentName && route.parentName.indexOf(keyword) > -1)

					items = items.filter((x) => {
						return keywords.map(keyword => isKeywordInText(x, keyword)).every(status => status === true)
					})
				}

				const rawSelectedId = this.selectedItem ? (typeof this.selectedItem === 'string' ? this.selectedItem : (this.selectedItem.instanceId || this.selectedItem.id)) : null
				const selectedBaseId = rawSelectedId ? (typeof rawSelectedId === 'string' ? rawSelectedId.replace(/_\d+$/, '') : rawSelectedId) : null
				const selectedItemId = this.selectedItem ? (typeof this.selectedItem === 'string' ? this.selectedItem : this.selectedItem.id) : null

				const isTreeActive = (node) => {
					if (!selectedBaseId) return false
					if (node.id === selectedBaseId || (selectedItemId && node.id === selectedItemId)) return true
					if (Array.isArray(node.items)) {
						return node.items.some(ch => isTreeActive(ch))
					}
					if (Array.isArray(node.child)) {
						return node.child.some(ch => isTreeActive(ch))
					}
					return false
				}

				const changeFormat = (item) => {
					delete item.component
					if (item.icon && !item.icon.startsWith('fad fa-') && !item.icon.startsWith('fa-') && !item.icon.startsWith('fas ') && !item.icon.startsWith('fal ')) {
						item.icon = 'fad fa-' + item.icon
					}
					if (this.activeMode === 'all' && this.searchKeyword && item.parentName) {
						item.title = `${item.parentName} - ${item.menu}`
					} else {
						item.title = item.menu
					}
					item.child = item.items
					item.disabled = !item.hasCommercialAccess || item.isDisabled

					const isSelfActive = !!(selectedBaseId && (item.id === selectedBaseId || (selectedItemId && item.id === selectedItemId)))
					const hasActiveDescendant = !isSelfActive && isTreeActive(item)

					item.isActive = () => isSelfActive

					let classes = []
					if (!item.hasCommercialAccess) classes.push('no-access')
					if (hasActiveDescendant) classes.push('vsm--item_tree-active')
					if (isSelfActive) classes.push('vsm--item_self-active')
					item.class = classes.join(' ')

					item.attributes = item.isDisabled ? { 'title': this.$t('no-access') } : {}
					if (Array.isArray(item.child)) {
						item.child.forEach(ch => changeFormat(ch))
					}
				}
				items.forEach(ch => changeFormat(ch))
				return items
			},
		},
		created() {
			this.clone()
			if (!this.backVersion) {
				this.localFrontVersion = versionDetailsJson.reduce((a, c) => {
					c.frontVersion >= a ? a = c.frontVersion : null
					return a
				}, 0)
				get(Endpoints.getUrl('general.version'), null, { ignoreCommercialAccess: true })
					.then((result) => {
						let splitBackVersions = result.data.split('.')
						this.localBackVersion = splitBackVersions.slice(0, splitBackVersions.length - 1).join('.')
					})
					.catch(() => {
						this.localBackVersion = 'unable to get backend version'
					})
			}
		},
		mounted() {
			// this.subscribeKeys()
		},
		methods: {
			openAnnouncementsModal() {
				this.$emit('openAnnouncements')
			},
			appRefreshClicked() {
				this.showModal({
					component: cacheCleaner,
				}).catch(() => { })
			},
			checkVersion(forceToShowTab = false) {
				const localAppVersion = window.localStorage.getItem('app-version')
				const versionStr = this.displayVersion

				if (this.effectiveBackVersion && this.effectiveFrontVersion) {
					if ((localAppVersion !== versionStr) || forceToShowTab) {
						window.localStorage.setItem('app-version', versionStr)
						const foundVersion = versionDetailsJson.find(x => x.backVersion + '.' + x.frontVersion === versionStr)
						if (!!foundVersion || forceToShowTab) {
							let route = routes.find(x => x.id == 'version-detail') || findRoute('version-detail')
							if (route) {
								route = _.cloneDeep(route)
								route.options = { currentBackVersion: this.effectiveBackVersion, currentFrontVersion: this.effectiveFrontVersion, versions: versionDetailsJson }
								this.$emit('navigate', route)
							}
						}
					}
				}
			},
			openFavoriteSettings() {
				const route = this.searchInRoutesById('settings-general') || findRoute('settings-general') || { id: 'settings-general' }
				this.$emit('navigate', route)
			},
			setMode(mode) {
				if (this.activeMode === mode) return
				this.isAnimating = true
				this.activeMode = mode
				this.defer(() => {
					this.isAnimating = false
				}, 300)
			},
			getLeafRoutes(list) {
				let result = []
				if (!Array.isArray(list)) return result
				for (const item of list) {
					if (item.items && item.items.length) {
						result = result.concat(this.getLeafRoutes(item.items))
					} else if (item.component || item.func || item.isDynamicRoute) {
						result.push(item)
					}
				}
				return result
			},
			getMostUsedRoutes() {
				const leaves = this.getLeafRoutes(this.routes)
				const counts = this.getTabUsageCounts || {}
				let items = _.cloneDeep(leaves)

				// Only items that have been used
				items = items.filter(x => (counts[x.id] || 0) > 0)

				// Sort descending by usage count
				items.sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0))

				// Max 10 items
				return items.slice(0, 10)
			},
			getFavoriteRoutes() {
				const favState = this.getActiveState('favoriteTabs')
				let items = []

				if (favState && favState.value && Array.isArray(favState.value.checkedTabs) && favState.value.checkedTabs.length) {
					for (const checkedItem of favState.value.checkedTabs) {
						const fullRoute = this.searchInRoutesById(checkedItem.id)
						if (fullRoute) {
							items.push(_.cloneDeep(fullRoute))
						} else {
							items.push(_.cloneDeep(checkedItem))
						}
					}
				}

				return items
			},
			getRecentRoutes() {
				const recent = this.getRecentTabs || []
				let items = []
				const reversed = [...recent].reverse()
				const seenIds = new Set()

				for (const tab of reversed) {
					const baseId = tab.instanceId || (typeof tab.id === 'string' ? tab.id.split('_')[0] : tab.id)
					if (baseId && baseId !== 'home' && !seenIds.has(baseId)) {
						seenIds.add(baseId)
						const fullRoute = this.searchInRoutesById(baseId)
						if (fullRoute) {
							items.push(_.cloneDeep(fullRoute))
						} else {
							items.push(_.cloneDeep(tab))
						}
					}
				}

				// Max 10 items
				return items.slice(0, 10)
			},
			prepareDynamicRoutes() {
				let preload = Promise.all([
					this.prepareDynamicReportRoutes(),
					this.prepareCustomAccountCrawlingRoutes()
				])

				preload.finally(() => {
					this.sidebarToggler = true
				})
			},
			prepareDynamicReportRoutes() {
				return post(Endpoints.getUrl('reportsDynamic.list')).then(res => {
					let dynamicReports = res.data.items

					for (let report of dynamicReports) {
						if (report.navKey) {
							let routeObj = {
								id: report.id,
								func: () => {
									dynamicReportInstance.launchDynamicReport(report.id, this)
								},
								menu: report.name,
								icon: 'chart-line',
								isDynamicRoute: true
							}

							let target = routes
							for (let item of report.navKey.split('.')) {
								target = target.find(x => x.id === item).items
							}

							target.push(routeObj)
						}
					}
				})
					.catch(() => { })
					.finally(() => {
					})
			},
			prepareCustomAccountCrawlingRoutes() {
				return post(Endpoints.getUrl('customAccountCrawling.list')).then(res => {
					let reports = res.data.items

					for (let report of reports) {
						if (!report.navKey)
							continue

						const route = findRoute('reports.custom-account-crawling-viewer')
						if (!route)
							continue

						let routeObj = _.cloneDeep(route)
						routeObj.id = routeObj.id + report.id
						// console.log("🚀 ~ returnpost ~ routeObj.id:", routeObj.id)
						routeObj.menu = report.name
						routeObj.component = markRaw(routeObj.component)
						routeObj.hidden = false
						routeObj.data = report
						routeObj.options = { autoLoad: true }
						routeObj.isDynamicRoute = true

						let target = routes
						for (let item of report.navKey.split('.')) {
							target = target.find(x => x.id === item).items
						}

						target.push(routeObj)
					}
				})
					.catch(() => { })
					.finally(() => {
					})
			},
			openInitTabs(currentHash) {
				var startups = this.routes.filter((x) => x.startup === true)
				if (startups.length > 0) {
					for (var i = 0; i < startups.length; i++) {
						this.$emit('navigate', startups[i])
					}
				}

				const hash = currentHash || this.hash

				if (hash !== '') {
					let directTab = this.find(hash)
					if (directTab != null) {
						directTab.query = this.$route.query
						this.$emit('navigate', directTab)
					}
				}

			},

			clone() {
				const iterate = (list, parentName = '') => {
					for (var r of list) {
						if (r.menu && r.menu.startsWith('nav.')) {
							r.menu = this.$t(r.menu)
						}

						r.pageTitle = r.pageTitle || r.menu
						if (r.pageTitle.startsWith('nav.')) {
							r.pageTitle = this.$t(r.pageTitle)
						}

						r.href = r.component ? r.id : ''
						r.parentName = parentName

						if (r.items && r.items.length) {
							r.items = r.items.filter(item => !item.isDynamicRoute)
							iterate(r.items, r.pageTitle)
						}
					}
				}

				iterate(this.routes)
			},
			menuFocused() {
				this.isFocused = true
				this.emitFocus(this.selectedItem || _.head(this.filteredRoutes))
			},
			menuBlur() {
				this.isFocused = false
				this.closeMenu()
				this.emitFocus(null)
			},
			toggleCollapsed(event) {
				this.collapsed = !this.collapsed
				this.onToggleCollapse(this.collapsed)
			},
			navigate(route) {
				this.isFocused = false
				this.closeMenu()
				this.emitFocus(null)
				this.$emit('navigate', route)
			},
			closeMenu() {
				this.$emit('openedItemChanged', null)
			},
			openedItemChanged(route) {
				this.$emit('openedItemChanged', route)
			},
			find(hash) {
				const findInChildren = (parent) => {
					if (parent.id === hash) {
						return parent
					} else if (parent.items && parent.items.length) {
						for (let item of parent.items) {
							let result = findInChildren(item)
							if (result != null) {
								return result
							}
						}
					}

					return null
				}

				for (let item of this.routes) {
					let result = findInChildren(item)
					if (result != null) {
						return result
					}
				}

				return null
			},

			emitFocus(item) {
				this.$emit('focusedItemChanged', item)
			},
			findParent(target) {
				const findIn = (item) => {
					if (item === target) {
						return null
					}

					if (item.items) {
						for (let i = 0; i < item.items.length; i++) {
							let current = item.items[i]
							if (current === target) {
								return item
							} else if (current.items && current.items.length) {
								let parent = findIn(current)
								if (parent) {
									return parent
								}
							}
						}
					}

					return null
				}

				for (let i = 0; i < this.filteredRoutes.length; i++) {
					let parent = findIn(this.filteredRoutes[i])
					if (parent) {
						return parent
					}
				}

				return null
			},
			navItemAreEqual(first, second) {
				if (!first ^ !second || !first) {
					return false
				}

				return first.id === second.id
			},
			onToggleCollapse(collapsed) {
				$('#main-sidebar').css('width', collapsed ? '65px' : '255px')
			},
			onItemClick(event, item) {
				if (this.isOpening) return

				this.isOpening = true

				this.isFocused = false
				let route = this.searchInRoutesById(item.id)

				if (route && route.func)
					this.$emit('executeFunction', route)

				else if (route && route.component)
					this.$emit('navigate', route)

				this.defer(() => {
					this.isOpening = false
				}, 500)
			},
			searchInRoutesById(id) {
				const search = (routes) => {
					let route = routes.find(x => x.id === id)
					if (route)
						return route
					route = null
					for (let index = 0; index < routes.length; index++) {
						if (Array.isArray(routes[index].items)) {
							route = search(routes[index].items)
							if (route)
								return route
						}
					}
					return route
				}
				return search(this.routes)
			},
			subscribeKeys() {
				// console.log('subscribeKeys2',this.parentEl())
				// console.log('',this.$keyHandler.Handlers)

				/*this.$keyHandler.Subscribe({*/
				/*	name: `arrow-button-asd65s`,*/
				/*	// keyCode: 40, // 40:arrow-down*/
				/*  keyCode: 40, ctrlKey: true,*/
				/*	el: this.parentEl(),*/
				/*	context: this,*/
				/*	handler: function (sender, e) {*/
				/*    console.log('arrow-down',)*/
				/*		// e.preventDefault()*/
				/*    //*/
				/*		// const focusNextOf = (item) => {*/
				/*		// 	let parent = this.findParent(item)*/
				/*		// 	if (parent) {*/
				/*		// 		let index = parent.items.indexOf(item)*/
				/*		// 		if (index < parent.items.length - 1) {*/
				/*		// 			focusFirstChild(parent.items[index + 1])*/
				/*		// 		} else {*/
				/*		// 			focusNextOf(parent)*/
				/*		// 		}*/
				/*		// 	} else {*/
				/*		// 		for (let i = 0; i < this.filteredRoutes.length; i++) {*/
				/*		// 			if (*/
				/*		// 				this.navItemAreEqual(this.filteredRoutes[i], item) &&*/
				/*		// 				i < this.filteredRoutes.length - 1*/
				/*		// 			) {*/
				/*		// 				focusFirstChild(this.filteredRoutes[i + 1])*/
				/*		// 			}*/
				/*		// 		}*/
				/*		// 	}*/
				/*		// }*/
				/*    //*/
				// 		// const focusFirstChild = (item) => {
				// 		// 	if (
				// 		// 		!item.items ||
				// 		// 		!item.items.length ||
				// 		// 		!this.openedItem ||
				// 		// 		this.openedItem.id !== item.id
				// 		// 	) {
				// 		// 		this.emitFocus(item)
				// 		// 	} else {
				// 		// 		this.emitFocus(item.items[0])
				// 		// 	}
				// 		// }
				//     //
				// 		// if ($(this.$refs.searchInput).is(':focus')) {
				// 		// 	this.$el.focus()
				// 		// 	this.emitFocus(_.head(this.filteredRoutes))
				// 		// } else {
				// 		// 	let targetItem =
				// 		// 		this.focusedItem ||
				// 		// 		this.selectedItem ||
				// 		// 		_.head(this.filteredRoutes)
				// 		// 	if (targetItem) {
				// 		// 		focusNextOf(targetItem)
				// 		// 	}
				// 		// }
				//     //
				// 		// return true
				// 	},
				// })
				// console.log('',this.$keyHandler.Handlers)
			},
		},
	}
</script>
<style>
	.nk-search input {
		background-color: transparent;
		border: none !important;
		outline: none;
		color: #aaa;
	}

	.nk-search input::placeholder {
		color: #aaa;
	}

	.slide-enter-active,
	.slide-leave-active {
		transition: max-height 0.35s;
	}

	.slide-enter,
	.slide-leave-to {
		max-height: 0px;
	}

	.slide-enter-to,
	.slide-leave {
		max-height: 20em;
	}
</style>
